import React from 'react';
import Sparkline from './Sparkline';
import RingProgress from './RingProgress';
import { AnimatedCard } from '../animations/AnimatedCard';
import { AnimatedNumber } from '../animations/AnimatedNumber';

type Props = {
  label: string;
  value: string;
  /** Si fourni, anime le chiffre avec CountUp (value reste le titre / fallback). */
  numericValue?: number;
  valueSuffix?: string;
  valueDecimals?: number;
  change: number;
  changeLabel?: string;
  sparkValues?: number[];
  sparkColor?: string;
  invertChange?: boolean;
  ringValue?: number;
  ringColor?: string;
  className?: string;
  delay?: number;
};

export default function MetricCard({
  label,
  value,
  numericValue,
  valueSuffix = '',
  valueDecimals,
  change,
  changeLabel = 'vs mois dernier',
  sparkValues,
  sparkColor = '#00E676',
  invertChange = false,
  ringValue,
  ringColor = '#22D3EE',
  className = '',
  delay = 0,
}: Props) {
  const effective = invertChange ? -change : change;
  const isUp = effective > 0;
  const isFlat = Math.abs(change) < 0.05;
  const changeColor = isFlat
    ? 'text-dark-400'
    : isUp
      ? 'text-primary-400'
      : 'text-red-400';
  const changeBg = isFlat
    ? 'bg-white/[0.04]'
    : isUp
      ? 'bg-primary-500/10'
      : 'bg-red-500/10';

  const chart =
    typeof ringValue === 'number' ? (
      <RingProgress value={ringValue} color={ringColor} size={48} strokeWidth={5} />
    ) : sparkValues && sparkValues.length > 1 ? (
      <Sparkline values={sparkValues} color={sparkColor} width={88} height={36} />
    ) : null;

  return (
    <AnimatedCard className={`card-premium ${className}`} delay={delay}>
      <div className="relative z-[1] flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <p className="text-body-sm font-medium text-dark-400">{label}</p>
          {chart && <div className="shrink-0 opacity-95">{chart}</div>}
        </div>

        <p className="kpi-value" title={value}>
          {typeof numericValue === 'number' ? (
            <AnimatedNumber
              value={numericValue}
              suffix={valueSuffix}
              decimals={valueDecimals}
            />
          ) : (
            value
          )}
        </p>

        <div
          className={`inline-flex max-w-full flex-wrap items-center gap-1 rounded-full px-2.5 py-1 text-caption font-semibold ${changeBg} ${changeColor}`}
        >
          <span>{isFlat ? '→' : isUp ? '↗' : '↘'}</span>
          <span>
            {change > 0 ? '+' : ''}
            {change.toFixed(1).replace('.', ',')} %
          </span>
          <span className="font-medium text-dark-500">{changeLabel}</span>
        </div>
      </div>
    </AnimatedCard>
  );
}
